/**
 * O `UI_REGISTRY` — spec-001 do `project-plataforma-por-chat`, [T004] / US-2.
 *
 * É o ÚNICO lugar onde uma chave de cartão vira um componente Vue. O backend
 * manda `ui.component: 'fee-fixo/simulacao-card'` na parte da tool; o front
 * resolve aqui, valida os props contra o schema e monta com `<component :is>`.
 * O ganho de ter um só ponto: um cartão novo passa a ser UMA entrada num
 * registry parcial de módulo, sem tocar no `chat-message.vue`.
 *
 * ## As quatro recusas têm nomes diferentes de propósito
 *
 * Toda falha de resolução devolve um `motivo`, e o motivo vai para o
 * `fallback-card`. Colapsar os quatro num "erro" genérico apagaria exatamente
 * a informação que decide o que fazer:
 *
 * | motivo | o que aconteceu | quem resolve |
 * |---|---|---|
 * | `chave_desconhecida` | o backend estreou um cartão que este front não tem | deploy do front |
 * | `flag_desligada` | o escritório não tem o módulo | comercial/config |
 * | `estado_nao_suportado` | o cartão não sabe desenhar aquela fase da tool | dev do cartão |
 * | `schema` | os props vieram fora do contrato | dev do backend/tool |
 *
 * ## Fail-closed na flag
 *
 * Sem o mapa `show` (o `store.getters['parameters/show']`), uma entrada com
 * `feature` é tratada como DESLIGADA. É a escolha conservadora: o outro lado
 * do erro é mostrar dado de um módulo que o escritório não contratou dentro da
 * conversa, e isso não tem desfazer.
 *
 * ## Puro de propósito
 *
 * Este arquivo não importa a store nem o router — `show`, `state` e `tool`
 * chegam por argumento. É o que permite testar as recusas sem montar um
 * componente, e é o que impede o registry de virar um segundo lugar onde a
 * store é lida.
 */
import { validar } from './validar'

/** Os motivos de recusa. O `fallback-card` humaniza cada um. */
export const MOTIVOS = {
  CHAVE_DESCONHECIDA: 'chave_desconhecida',
  FLAG_DESLIGADA: 'flag_desligada',
  SCHEMA: 'schema',
  ESTADO_NAO_SUPORTADO: 'estado_nao_suportado',
}

/**
 * Ponte do mundo antigo: `nome_da_tool` → chave do registry.
 *
 * Existe para a mensagem PERSISTIDA antes desta spec, e para a tool que ainda
 * não declara `ui` no backend. Nasce vazio nesta task — quem preenche são as
 * tasks dos registries parciais ([T016] fee-fixo, [T017] iv2/painel/kernel/bi).
 * Quando `ui.component` vier na parte, ele tem precedência: o backend sabe
 * melhor que o mapa.
 */
/** Convenção do MCP: `mcp__<servidor>__<tool>` — ver `_semPrefixoDeMcp`. */
const PREFIXO_DE_MCP = 'mcp__'

export const LEGACY_TOOL_TO_UI = {
  // [spec-021 T007] o andamento do documento gerado — a tool nao declarava
  // `ui`, entao o consultor recebia so' o protocolo em markdown.
  gerar_apresentacao: 'fee-fixo/apresentacao',
  // ── [T017] iv2 · Consultor ────────────────────────────────────────────────
  // ★ #27 — `ler_extrato` é a ÚNICA write-tool da segunda via
  // (`TOOLS_COM_PROTOCOLO_PROPRIO`) que NÃO declara `UI_SCHEMA` próprio:
  // `gerar_proposta_r2` e `popular_campos_da_reuniao` trazem `ui` no evento
  // (pelo #213) e se resolvem sozinhas. Sem `ui` e sem entrada aqui,
  // `chaveDaParte` devolve `null` e `chat-message.vue::partesDeTools` faz
  // `if (!chave) return false` — a proposta era DESCARTADA e o consultor via
  // o chip 'aguardando decisão' sem nenhum botão.
  //
  // Medido em produção (7 dias até 19/09/2026): 25 de 27 propostas do Modo
  // Agente terminaram em `continuacao_apos_expiracao` — sem cartão, não há
  // onde clicar. O cartão GENÉRICO serve: ele se monta com os metadados do
  // próprio evento (`resumo`/`entidade`/`operacao`/`preview`), que é o que
  // `_propostas_eventos_apoio.py` publica.
  ler_extrato: 'crm/confirmacao',
  citar_servicos: 'crm/servicos-citados',
  perguntar_ao_gestor: 'crm/perguntas-em-aberto',
  convocar_painel: 'consultor/painel-deliberacao',

  // ── [T017] iv2 · as dez write-tools GENÉRICAS do CRM ──────────────────────
  // Espelho manual de `ToolRegistry.WRITE_TOOL_NAMES`
  // (`aawz_intelligence_v2/tools/tool_registry.py:238-278`) MENOS as três do Fee
  // Fixo, que têm cartão próprio (`fee-fixo/confirmacao`, `fee-fixo/simulacao-card`
  // — `contracts/ui-components.md` §3). O front não importa o backend; a lista
  // vive literal aqui e no `ia/registry.unit.js`, que compara as duas.
  //
  // ★ O que este mapa NÃO decide: se o cartão aparece, e em QUAL forma. Preview
  // e commit são a MESMA tool com o mesmo nome; quem os separa é o `state`
  // (`approval-requested` × `output-available`, `core/sse.py:_mapear_tool_result`),
  // e é dele que sai o desfecho na conversa reaberta ([G-1], em
  // `partes-do-turno/partes.js`). O mapa diz de quem é o cartão; o estado diz
  // qual das formas dele desenhar.
  create_tarefa: 'crm/confirmacao',
  edit_tarefa: 'crm/confirmacao',
  // spec-002 comentar-tarefa-tool — comentário ≠ descrição, mesmo cartão
  comentar_tarefa: 'crm/confirmacao',
  create_oportunidade: 'crm/confirmacao',
  edit_oportunidade: 'crm/confirmacao',
  edit_atividade_crm: 'crm/confirmacao',
  create_ativo: 'crm/confirmacao',
  create_movimentacao: 'crm/confirmacao',
  edit_cliente_dados: 'crm/confirmacao',
  create_projeto_avaliacao: 'crm/confirmacao',
  mover_tarefas: 'crm/confirmacao',

  // ── [T016] fee-fixo · oito tools, seis cartões ────────────────────────────
  // Os nomes são espelho de `aawz_intelligence_v2/tools/tool_fee_fixo.py` (o
  // front não importa o backend); as chaves são as do parcial em
  // `@modules/fee-fixo/ia/registry`. `registry.unit.js` de lá compara as duas
  // pontas — contra as constantes de tool do módulo e contra o arquivo do
  // backend, lido como texto.
  //
  // Duas tools caem no MESMO atalho de cliente porque são duas formas do mesmo
  // payload (`cliente` singular e `clientes[]`), e as duas escritas caem na
  // MESMA confirmação porque o preview delas tem o mesmo shape — o que muda é
  // a copy, e ela sai da `operacao` que vem dentro do `value`.
  criar_simulacao_fee_fixo: 'fee-fixo/simulacao-card',
  sugerir_filtro_clientes: 'fee-fixo/recorte-card',
  estado_cliente_fee_fixo: 'fee-fixo/cliente-atalho-card',
  priorizar_carteira_fee_fixo: 'fee-fixo/cliente-atalho-card',
  analisar_rascunho_simulacao: 'fee-fixo/sugestao-rascunho',
  adicionar_clientes_migracao_fee_fixo: 'fee-fixo/confirmacao',
  registrar_desfecho_migracao_fee_fixo: 'fee-fixo/confirmacao',
  preparar_envio_relatorio_fee_fixo: 'fee-fixo/envio-relatorio',
}

/**
 * O agregado. Mutado no lugar por `registrar`, nunca reatribuído — quem
 * importou o objeto continua vendo o mesmo registry.
 */
export const UI_REGISTRY = {}

// `<modulo>/<cartao>`, kebab-case dos dois lados.
const CHAVE_VALIDA = /^[a-z0-9-]+\/[a-z0-9-]+$/

const temProprio = (objeto, chave) =>
  Object.prototype.hasOwnProperty.call(objeto, chave)

const recusar = (chave, o_que) => {
  throw new Error(
    'UI_REGISTRY: entrada "' + chave + '" inválida — ' + o_que + '.'
  )
}

/**
 * O que cartão e tool de front exigem em comum: chave no padrão, chave livre e
 * `label` (o rótulo humano que substitui o antigo `TOOL_LABELS`).
 */
const conferirComum = (chave, entrada) => {
  if (!CHAVE_VALIDA.test(chave)) {
    recusar(chave, 'a chave deve ser <modulo>/<cartao> em kebab-case')
  }
  if (temProprio(UI_REGISTRY, chave)) {
    recusar(
      chave,
      'a chave já está registrada; dois módulos disputando a mesma chave se ' +
        'sobrescreveriam em silêncio'
    )
  }
  if (!entrada || typeof entrada !== 'object') {
    recusar(chave, 'a entrada precisa ser um objeto')
  }
  if (!entrada.label || typeof entrada.label !== 'string') {
    recusar(chave, 'falta label (rótulo humano da tool/cartão)')
  }
}

/** Cartão: precisa saber o que montar, com que contrato e em que estados. */
const conferirCartao = (chave, entrada) => {
  if (typeof entrada.schema !== 'function') {
    recusar(chave, "falta schema — use `() => import('....schema.json')`")
  }
  if (!entrada.module || typeof entrada.module !== 'string') {
    recusar(chave, 'falta module (chave do catálogo de navegação)')
  }
  if (!Array.isArray(entrada.states) || entrada.states.length === 0) {
    recusar(chave, 'falta states (os estados que o cartão sabe renderizar)')
  }
}

/**
 * Confere UMA entrada antes de ela entrar no agregado.
 *
 * Cartão e tool de front têm contratos diferentes, e o contrato
 * (`contracts/ui-components.md` §1) mostra os dois: o cartão precisa de
 * `component`/`schema`/`module`/`states`; a tool de front (`plataforma/navegar`)
 * tem `executor: 'client'` + `handler` e nenhum dos quatro.
 */
const conferir = (chave, entrada) => {
  conferirComum(chave, entrada)

  if (entrada.component) {
    conferirCartao(chave, entrada)
    return
  }

  if (entrada.executor === 'client') {
    if (typeof entrada.handler !== 'function') {
      recusar(chave, 'tool de front sem handler')
    }
    return
  }

  recusar(
    chave,
    "não é cartão (sem component) nem tool de front (sem executor: 'client')"
  )
}

/**
 * Agrega o registry PARCIAL de um módulo.
 *
 * @param {Object} parcial  Mapa `chave → entrada`.
 * @returns {Object} O `UI_REGISTRY` agregado (o mesmo objeto, sempre).
 */
export function registrar(parcial) {
  const entradas = parcial || {}
  const chaves = Object.keys(entradas)

  // Confere TODAS antes de escrever QUALQUER uma: um parcial com uma entrada
  // ruim não pode deixar metade do módulo registrado.
  chaves.forEach((chave) => conferir(chave, entradas[chave]))
  chaves.forEach((chave) => {
    UI_REGISTRY[chave] = entradas[chave]
  })

  return UI_REGISTRY
}

/** As chaves registradas, em ordem estável (útil em teste e em diagnóstico). */
export function listarChaves() {
  return Object.keys(UI_REGISTRY).sort()
}

/** Esvazia o agregado MANTENDO a referência. Existe para os testes. */
export function limparRegistry() {
  Object.keys(UI_REGISTRY).forEach((chave) => {
    delete UI_REGISTRY[chave]
  })
  return UI_REGISTRY
}

/**
 * ★ O cartão da proposta GENÉRICA de escrita — o que o Modo Agente produz.
 *
 * `crm/confirmacao` já é o cartão das dez writes genéricas do CRM, e o
 * contrato dele (`ui-crm-confirmacao.schema.json`) é exatamente o envelope que
 * o agente manda. Reaproveitá-lo não é atalho: é o mesmo fato — "uma escrita
 * pede autorização" — desenhado pela peça que já sabe desenhá-lo.
 */
export const CHAVE_DA_PROPOSTA_GENERICA = 'crm/confirmacao'

/** Os campos que só existem no preview RICO, depois de a tool RODAR. */
const CAMPOS_DO_PREVIEW_RICO = ['inputs', 'premissas_declaradas', 'itens']

/**
 * ★ Este payload é a proposta GENÉRICA de escrita do Modo Agente?
 *
 * **Por que a pergunta existe — e o que eu escrevi errado aqui antes.** No
 * caminho SSE do Modo Chat a aprovação publica DOIS eventos (`core/sse.py`): o
 * `tool-output-available` com o `value` COMPLETO da tool e, ADICIONAL, o
 * `tool-approval-request`. No Modo Agente o transporte publica UM evento só,
 * com os METADADOS da proposta — o `value` da tool nunca é publicado. O que
 * chega ao front é, então, o envelope genérico:
 * `{requer_confirmacao, resumo, entidade, operacao, *_label}`.
 *
 * ⚠️ **A tool PRODUZ o valor rico.** Medido em 18/09/2026 pela sessão
 * `aawz-ia-demonstracao`, executando `ToolCriarSimulacaoFeeFixo` no pod de
 * produção: status 200, os cinco campos obrigatórios, zero erros contra
 * `ui-fee-fixo-simulacao-card.schema.json`. A primeira versão deste comentário
 * dizia que os campos ricos "não existem ainda, por desenho" — era **falso**, e
 * fica registrado porque a frase errada explicava o defeito pelo lugar errado.
 * O defeito é do TRANSPORTE, e o conserto dele mora no backend.
 *
 * ★ **Esta função é a REDE DE SEGURANÇA, não o conserto.** Quando o transporte
 * publicar o `value` rico, ela deixa de valer sozinha — a detecção exclui
 * `inputs`/`premissas_declaradas`/`itens` — e o cartão volta a ser o rico, com
 * premissas e economia à vista. O que ela impede é o outro desfecho: só o
 * envelope genérico chegar e o consultor ler "Não consegui montar este cartão"
 * com a run parada segurando créditos.
 *
 * O mapa `LEGACY_TOOL_TO_UI` responde pela TOOL, e a tool é a mesma nos dois
 * modos. Sem esta pergunta, a proposta do agente para
 * `criar_simulacao_fee_fixo` era mandada ao cartão do preview rico, reprovava
 * no schema por `inputs`/`premissas_declaradas` e o consultor lia **"Não
 * consegui montar este cartão · dados fora do contrato"** — com a run parada em
 * `awaiting_approval` segurando a reserva de créditos, porque o botão que
 * aprova mora no cartão que não desenhou (medido em produção, 18/09/2026, run
 * `d3abf662`, escritório 83).
 *
 * ⚠️ **A detecção é POSITIVA e exclui os campos ricos** — as duas coisas. Só
 * "tem `entidade`" pegaria o preview rico do Modo Chat e o rebaixaria para o
 * cartão genérico, trocando um defeito por outro.
 *
 * ⚠️ **`ui.component` continua vencendo** (em `resolverEntrada`): instrução
 * explícita do backend nunca perde para inferência nossa.
 */
export function ehPropostaGenericaDeEscrita(props) {
  if (!props || typeof props !== 'object') return false
  if (props.requer_confirmacao !== true) return false
  if (!props.entidade) return false
  const temRico = CAMPOS_DO_PREVIEW_RICO.some(function (campo) {
    return props[campo] !== undefined && props[campo] !== null
  })
  return !temRico
}

/**
 * ★ [spec-008 T082] O nome da tool SEM o prefixo de MCP.
 *
 * O provedor expõe a mesma tool por dois nomes, e isso foi medido numa sessão
 * paga (G7, 19/09/2026): na repetição 1 a proposta veio como `create_tarefa` e
 * o cartão desenhou; na repetição 2, **o mesmo pedido** veio como
 * `mcp__aawz__create_tarefa` e o `LEGACY_TOOL_TO_UI` — que casa por nome
 * EXATO — não achou nada. Resultado na tela: `chave_desconhecida`, nenhum
 * cartão, **86 botões e nenhum "Confirmar"**, com a proposta viva em
 * `approval-requested` e a bolha dizendo *"Aguardando sua decisão 161s"*.
 *
 * ⚠️ Isso não é cosmético: **sem cartão não há como decidir**. A escrita fica
 * pendurada até expirar, e o consultor vê um pedido de confirmação que não
 * tem onde ser confirmado.
 *
 * A convenção do MCP é `mcp__<servidor>__<tool>`; o corte é no SEGUNDO `__`,
 * e o resto volta junto porque nome de tool pode conter `__`.
 */
export function semPrefixoDeMcp(tool) {
  const nome = String(tool || '')
  if (nome.indexOf(PREFIXO_DE_MCP) !== 0) return nome
  // Sem guarda de tamanho DE PROPÓSITO: com menos de três segmentos o corte
  // devolve `''`, que erra o mapa exatamente como o nome inteiro erraria. Uma
  // guarda aqui seria ramo que nenhum teste consegue separar — e ramo
  // inseparável é cobertura de mentira (mutação sobrevivente, medida).
  return nome.split('__').slice(2).join('__')
}

/**
 * A chave pela TOOL — com as exceções que o mapa não sabe responder.
 *
 * O mapa diz de quem é o cartão; ele não sabe QUAL das duas formas do payload
 * chegou, porque preview rico e proposta genérica saem da mesma tool com o
 * mesmo nome e o mesmo `state`. Quando o payload é o envelope genérico, quem
 * desenha é o cartão genérico.
 *
 * ★ [T082] E quando o mapa NÃO conhece a tool, a pergunta que decide não é
 * *"qual cartão é o dela?"* e sim *"isto é uma escrita esperando decisão?"*.
 * Se for, ela ganha o cartão genérico: uma proposta sem cartão é uma proposta
 * que ninguém pode aprovar nem recusar. Antes daqui, tool fora do mapa caía
 * em `chave_desconhecida` e sumia da tela — que é como o defeito do G7 se
 * manifestou.
 */
function _chaveDaTool(ctx) {
  const doMapa = LEGACY_TOOL_TO_UI[semPrefixoDeMcp(ctx.tool)] || null
  if (!doMapa) {
    return ehPropostaGenericaDeEscrita(ctx.props) ? CHAVE_DA_PROPOSTA_GENERICA : null
  }
  if (doMapa === CHAVE_DA_PROPOSTA_GENERICA) return doMapa
  if (ehPropostaGenericaDeEscrita(ctx.props)) return CHAVE_DA_PROPOSTA_GENERICA
  return doMapa
}

/**
 * Qual cartão desenha esta parte de tool?
 *
 * @param {Object} contexto
 * @param {Object} [contexto.ui]     O `ui` da parte (`{ component, props }`).
 * @param {string} [contexto.tool]   Nome da tool, para o caminho legado.
 * @param {Object} [contexto.show]   `store.getters['parameters/show']`. Ausente
 *                                   = tudo desligado (fail-closed).
 * @param {string} [contexto.state]  Estado da tool part. Ausente = não checa.
 * @param {Object} [contexto.props]  O payload da parte. Só é consultado para
 *                                   separar as DUAS formas da mesma tool —
 *                                   ver `ehPropostaGenericaDeEscrita`.
 * @returns {{ chave: string|null, entrada?: Object, motivo?: string }}
 */
export function resolverEntrada(contexto) {
  const ctx = contexto || {}
  const ui = ctx.ui || {}
  const chave = ui.component || _chaveDaTool(ctx) || null
  const entrada = chave ? UI_REGISTRY[chave] : null

  if (!entrada) return { chave: chave, motivo: MOTIVOS.CHAVE_DESCONHECIDA }

  const show = ctx.show || {}
  if (entrada.feature && !show[entrada.feature]) {
    return { chave: chave, motivo: MOTIVOS.FLAG_DESLIGADA }
  }

  const estados = entrada.states
  if (
    ctx.state &&
    Array.isArray(estados) &&
    estados.indexOf(ctx.state) === -1
  ) {
    return { chave: chave, motivo: MOTIVOS.ESTADO_NAO_SUPORTADO }
  }

  return { chave: chave, entrada: entrada }
}

/**
 * Confere os props contra o schema da entrada. Assíncrono porque o schema é
 * lazy: o JSON só sai do bundle quando um cartão daquele tipo aparece.
 *
 * Separado do `resolverEntrada` (que é síncrono) porque a resolução decide o
 * que montar e a validação decide se PODE montar — e só a segunda paga o
 * custo de uma chunk. O chamador mapeia `ok: false` para `MOTIVOS.SCHEMA`.
 *
 * @returns {Promise<{ ok: boolean, erros: string[] }>}
 */
export function validarProps(entrada, props) {
  const carregador = entrada && entrada.schema
  if (typeof carregador !== 'function') {
    // Tool de front (e cartão legado sem contrato) não tem o que validar.
    return Promise.resolve({ ok: true, erros: [] })
  }

  return Promise.resolve()
    .then(() => carregador())
    .then((schema) => validar(schema, props))
    .catch((erro) => {
      // Chunk que não carrega vira recusa com o motivo à vista, nunca uma
      // promise rejeitada solta no meio do turno.
      const detalhe = erro && erro.message ? erro.message : String(erro)
      return {
        ok: false,
        erros: ['props: schema indisponível (' + detalhe + ')'],
      }
    })
}

// ─── Agregação dos registries parciais ──────────────────────────────────────
//
// Imports EXPLÍCITOS, um por módulo. NUNCA `require.context`: é API do webpack,
// não roda no Jest, e o registry ficaria vazio no teste sem nada ficar vermelho.
//
// Nesta task ([T004]) não há nenhum — os parciais chegam em [T016] (fee-fixo,
// 6 cartões), [T017] (iv2 + painel + kernel + bi) e [T018] (ai-framework).
// Quando chegarem, cada um entra como duas linhas:
//
//     import registryFeeFixo from '@modules/fee-fixo/ia/registry'
//     registrar(registryFeeFixo)
//
// ★ [T017]: o parcial do iv2 NÃO entrou aqui — ele se registra no ponto de
// entrada do módulo (`modules/aawz-intelligence-v2/index.js`, chamado no boot
// por `modules/index.js`), junto com o store. Registrar no IMPORT deste arquivo
// deixaria toda suíte que o importa começando com o agregado cheio, e os testes
// que montam entradas controladas (`limparRegistry()` + `registrar({...})`)
// passariam a depender da ordem de execução. O motivo do import explícito segue
// valendo para quem preferir agregar aqui: `require.context` NUNCA.

export default UI_REGISTRY
